import React, { useState, useEffect } from 'react';
import { 
  getUsers, updateUser, 
  getUserBilling, addPayment, deletePayment, getAdminStats,
  fullUpdateUser, changeUserPassword,
  getAllThemes, createTheme, updateTheme, deleteTheme,
  getTelegramSubscriptions, deleteTelegramSubscription, updateUserTelegramLimit
} from '../api';
import './AdminPanel.css';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('users'); // users, themes, telegram
  // === Пользователи ===
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [billing, setBilling] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: '', description: '', currency: 'RUB' });
  const [editForm, setEditForm] = useState({
    name: '', email: '', maxObjects: 5, validUntil: '', isActive: true, isAdmin: false, newPassword: ''
  });
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // === Темы ===
  const [themes, setThemes] = useState([]);
  const [themeForm, setThemeForm] = useState({
    name: '', displayName: '', cssVariables: {}, isActive: true, isDefault: false
  });
  const [editingThemeId, setEditingThemeId] = useState(null);

  // === Telegram ===
  const [subscriptions, setSubscriptions] = useState([]);
  const [telegramLimit, setTelegramLimit] = useState(5);
  const [selectedUserIdForLimit, setSelectedUserIdForLimit] = useState(null);

  useEffect(() => {
    loadUsers();
    loadStats();
    loadThemes();
    loadSubscriptions();
  }, []);

  // --- Пользователи ---
  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Ошибка загрузки пользователей:', err);
    }
  };

  const loadStats = async () => {
    try {
      const data = await getAdminStats();
      setStats(data);
    } catch (err) {
      console.error('Ошибка загрузки статистики:', err);
    }
  };

  const loadBilling = async (userId) => {
    try {
      const data = await getUserBilling(userId);
      setBilling(data);
    } catch (err) {
      console.error('Ошибка загрузки биллинга:', err);
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      maxObjects: user.maxObjects || 5,
      validUntil: user.validUntil ? new Date(user.validUntil).toISOString().split('T')[0] : '',
      isActive: user.isActive,
      isAdmin: user.isAdmin,
      newPassword: ''
    });
    setShowPasswordField(false);
    setMessage({ type: '', text: '' });
    loadBilling(user._id);
    setSelectedUserIdForLimit(user._id);
    setTelegramLimit(user.telegramSubscriptionLimit || 5);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const updateData = {
        name: editForm.name,
        email: editForm.email,
        maxObjects: parseInt(editForm.maxObjects),
        validUntil: editForm.validUntil || null,
        isActive: editForm.isActive,
        isAdmin: editForm.isAdmin
      };
      
      if (editForm.newPassword && editForm.newPassword.length >= 6) {
        updateData.password = editForm.newPassword;
        await fullUpdateUser(selectedUser._id, updateData);
        setMessage({ type: 'success', text: 'Пользователь обновлён (включая пароль)' });
      } else if (editForm.newPassword && editForm.newPassword.length < 6) {
        setMessage({ type: 'error', text: 'Пароль должен содержать минимум 6 символов' });
        setLoading(false);
        return;
      } else {
        await updateUser(selectedUser._id, updateData);
        setMessage({ type: 'success', text: 'Пользователь обновлён' });
      }
      
      await handleSelectUser({ ...selectedUser, ...updateData });
      loadUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePasswordOnly = async () => {
    if (!selectedUser || !editForm.newPassword) {
      setMessage({ type: 'error', text: 'Введите новый пароль' });
      return;
    }
    if (editForm.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Пароль должен содержать минимум 6 символов' });
      return;
    }
    setLoading(true);
    try {
      await changeUserPassword(selectedUser._id, editForm.newPassword);
      setMessage({ type: 'success', text: 'Пароль изменён' });
      setEditForm(prev => ({ ...prev, newPassword: '' }));
      setShowPasswordField(false);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async () => {
    if (!selectedUser || !newPayment.amount) return;
    try {
      await addPayment(selectedUser._id, newPayment);
      setNewPayment({ amount: '', description: '', currency: 'RUB' });
      loadBilling(selectedUser._id);
      setMessage({ type: 'success', text: 'Платёж добавлен' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!selectedUser) return;
    try {
      await deletePayment(selectedUser._id, paymentId);
      loadBilling(selectedUser._id);
      setMessage({ type: 'success', text: 'Платёж удалён' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  // --- Темы ---
  const loadThemes = async () => {
    try {
      const data = await getAllThemes();
      setThemes(data);
    } catch (err) {
      console.error('Ошибка загрузки тем:', err);
    }
  };

  const handleThemeFormChange = (field, value) => {
    setThemeForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCssVarChange = (key, value) => {
    setThemeForm(prev => ({
      ...prev,
      cssVariables: { ...prev.cssVariables, [key]: value }
    }));
  };

  const resetThemeForm = () => {
    setThemeForm({
      name: '', displayName: '', cssVariables: {}, isActive: true, isDefault: false
    });
    setEditingThemeId(null);
  };

  const handleSaveTheme = async () => {
    setLoading(true);
    try {
      if (editingThemeId) {
        await updateTheme(editingThemeId, themeForm);
        setMessage({ type: 'success', text: 'Тема обновлена' });
      } else {
        await createTheme(themeForm);
        setMessage({ type: 'success', text: 'Тема создана' });
      }
      resetThemeForm();
      loadThemes();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEditTheme = (theme) => {
    setEditingThemeId(theme._id);
    setThemeForm({
      name: theme.name,
      displayName: theme.displayName,
      cssVariables: theme.cssVariables || {},
      isActive: theme.isActive,
      isDefault: theme.isDefault
    });
  };

  const handleDeleteTheme = async (id, isDefault) => {
    if (isDefault) {
      setMessage({ type: 'error', text: 'Нельзя удалить тему по умолчанию' });
      return;
    }
    if (window.confirm('Удалить тему?')) {
      try {
        await deleteTheme(id);
        loadThemes();
        setMessage({ type: 'success', text: 'Тема удалена' });
      } catch (err) {
        setMessage({ type: 'error', text: err.message });
      }
    }
  };

  const handleToggleThemeActive = async (theme) => {
    try {
      await updateTheme(theme._id, { ...theme, isActive: !theme.isActive });
      loadThemes();
      setMessage({ type: 'success', text: `Тема ${theme.isActive ? 'деактивирована' : 'активирована'}` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  // --- Telegram ---
  const loadSubscriptions = async () => {
    try {
      const data = await getTelegramSubscriptions();
      setSubscriptions(data);
    } catch (err) {
      console.error('Ошибка загрузки подписок:', err);
    }
  };

  const handleDeleteSubscription = async (id) => {
    if (window.confirm('Удалить подписку?')) {
      try {
        await deleteTelegramSubscription(id);
        loadSubscriptions();
        setMessage({ type: 'success', text: 'Подписка удалена' });
      } catch (err) {
        setMessage({ type: 'error', text: err.message });
      }
    }
  };

  const handleUpdateTelegramLimit = async () => {
    if (!selectedUserIdForLimit) return;
    try {
      await updateUserTelegramLimit(selectedUserIdForLimit, telegramLimit);
      setMessage({ type: 'success', text: 'Лимит обновлён' });
      loadUsers(); // обновить список пользователей
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  return (
    <div className="admin-panel">
      <h1>Панель администратора</h1>
      
      {message.text && (
        <div className={`admin-message ${message.type}`}>
          {message.text}
        </div>
      )}
      
      <div className="admin-tabs">
        <button className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>
          Пользователи и биллинг
        </button>
        <button className={activeTab === 'themes' ? 'active' : ''} onClick={() => setActiveTab('themes')}>
          Управление темами
        </button>
        <button className={activeTab === 'telegram' ? 'active' : ''} onClick={() => setActiveTab('telegram')}>
          Telegram подписки
        </button>
      </div>

      {/* Вкладка пользователей */}
      {activeTab === 'users' && (
        <>
          <div className="admin-stats">
            <div className="stat-card"><h3>Всего пользователей</h3><div className="stat-value">{stats?.totalUsers || 0}</div></div>
            <div className="stat-card"><h3>Активных</h3><div className="stat-value">{stats?.activeUsers || 0}</div></div>
            <div className="stat-card"><h3>С истёкшей лицензией</h3><div className="stat-value">{stats?.expiredUsers || 0}</div></div>
            <div className="stat-card"><h3>Администраторов</h3><div className="stat-value">{stats?.adminCount || 0}</div></div>
          </div>
          <div className="admin-content">
            <div className="users-list">
              <h2>Пользователи</h2>
              <table>
                <thead><tr><th>Email</th><th>Имя</th><th>Статус</th><th>Лимит камер</th><th>Лимит Telegram</th><th>Действует до</th></tr></thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user._id} onClick={() => handleSelectUser(user)} className={selectedUser?._id === user._id ? 'selected' : ''}>
                      <td>{user.email}</td>
                      <td>{user.name || '-'}</td>
                      <td>{user.isActive ? '🟢 Активен' : '🔴 Заблокирован'}</td>
                      <td>{user.maxObjects}</td>
                      <td>{user.telegramSubscriptionLimit || 5}</td>
                      <td>{user.validUntil ? new Date(user.validUntil).toLocaleDateString() : '∞'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {selectedUser && (
              <div className="user-details">
                <h2>Редактирование пользователя</h2>
                <div className="form-group"><label>Имя</label><input type="text" value={editForm.name} onChange={(e) => handleEditFormChange('name', e.target.value)} /></div>
                <div className="form-group"><label>Email</label><input type="email" value={editForm.email} onChange={(e) => handleEditFormChange('email', e.target.value)} /></div>
                <div className="form-group"><label>Лимит камер</label><input type="number" value={editForm.maxObjects} onChange={(e) => handleEditFormChange('maxObjects', parseInt(e.target.value))} /></div>
                <div className="form-group"><label>Действует до</label><input type="date" value={editForm.validUntil} onChange={(e) => handleEditFormChange('validUntil', e.target.value)} /></div>
                <div className="form-group"><label><input type="checkbox" checked={editForm.isActive} onChange={(e) => handleEditFormChange('isActive', e.target.checked)} /> Активен</label></div>
                <div className="form-group"><label><input type="checkbox" checked={editForm.isAdmin} onChange={(e) => handleEditFormChange('isAdmin', e.target.checked)} /> Администратор</label></div>
                <div className="form-group"><label>Лимит Telegram подписок</label><input type="number" value={telegramLimit} onChange={(e) => setTelegramLimit(parseInt(e.target.value))} /><button onClick={handleUpdateTelegramLimit}>Сохранить лимит</button></div>
                <div className="form-group"><button onClick={() => setShowPasswordField(!showPasswordField)}>Изменить пароль</button></div>
                {showPasswordField && (
                  <div className="form-group"><label>Новый пароль</label><input type="password" value={editForm.newPassword} onChange={(e) => handleEditFormChange('newPassword', e.target.value)} /><button onClick={handleChangePasswordOnly}>Сменить пароль</button></div>
                )}
                <button onClick={handleSaveUser} disabled={loading}>Сохранить изменения</button>
                <h3>Биллинг</h3>
                <div className="add-payment">
                  <input type="number" placeholder="Сумма" value={newPayment.amount} onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })} />
                  <input type="text" placeholder="Описание" value={newPayment.description} onChange={(e) => setNewPayment({ ...newPayment, description: e.target.value })} />
                  <select value={newPayment.currency} onChange={(e) => setNewPayment({ ...newPayment, currency: e.target.value })}><option>RUB</option><option>USD</option><option>EUR</option></select>
                  <button onClick={handleAddPayment}>Добавить платёж</button>
                </div>
                <table className="payments-table">
                  <thead><tr><th>Дата</th><th>Сумма</th><th>Описание</th><th></th></tr></thead>
                  <tbody>
                    {billing?.payments?.map(p => (
                      <tr key={p._id}><td>{new Date(p.date).toLocaleDateString()}</td><td>{p.amount} {p.currency}</td><td>{p.description}</td><td><button onClick={() => handleDeletePayment(p._id)}>×</button></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Вкладка тем */}
      {activeTab === 'themes' && (
        <div className="themes-management">
          <div className="themes-list">
            <h2>Существующие темы</h2>
            <table>
              <thead><tr><th>Имя</th><th>Отображаемое имя</th><th>Активна</th><th>По умолчанию</th><th>Действия</th></tr></thead>
              <tbody>
                {themes.map(theme => (
                  <tr key={theme._id}>
                    <td>{theme.name}</td>
                    <td>{theme.displayName}</td>
                    <td>{theme.isActive ? '✅ Да' : '❌ Нет'}</td>
                    <td>{theme.isDefault ? '⭐ Да' : ''}</td>
                    <td>
                      <button onClick={() => handleEditTheme(theme)}>✏️ Редактировать</button>
                      <button onClick={() => handleToggleThemeActive(theme)}>{theme.isActive ? '🔴 Отключить' : '🟢 Включить'}</button>
                      <button onClick={() => handleDeleteTheme(theme._id, theme.isDefault)}>🗑️ Удалить</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="theme-form">
            <h2>{editingThemeId ? 'Редактировать тему' : 'Создать новую тему'}</h2>
            <div className="form-group"><label>Техническое имя</label><input type="text" value={themeForm.name} onChange={(e) => handleThemeFormChange('name', e.target.value)} /></div>
            <div className="form-group"><label>Отображаемое имя</label><input type="text" value={themeForm.displayName} onChange={(e) => handleThemeFormChange('displayName', e.target.value)} /></div>
            <div className="form-group"><label><input type="checkbox" checked={themeForm.isActive} onChange={(e) => handleThemeFormChange('isActive', e.target.checked)} /> Активна</label></div>
            <div className="form-group"><label><input type="checkbox" checked={themeForm.isDefault} onChange={(e) => handleThemeFormChange('isDefault', e.target.checked)} /> По умолчанию</label></div>
            <h4>CSS-переменные</h4>
            <textarea rows={6} value={JSON.stringify(themeForm.cssVariables, null, 2)} onChange={(e) => {
              try { setThemeForm(prev => ({ ...prev, cssVariables: JSON.parse(e.target.value) })); } catch(e) {}
            }} />
            <button onClick={handleSaveTheme} disabled={loading}>Сохранить тему</button>
            <button onClick={resetThemeForm}>Отмена</button>
          </div>
        </div>
      )}

      {/* Вкладка Telegram */}
      {activeTab === 'telegram' && (
        <div className="telegram-management">
          <h2>Управление подписками Telegram</h2>
          <div className="subscriptions-list">
            <table>
              <thead><tr><th>Токен объекта</th><th>Chat ID</th><th>Активна</th><th>Дата подписки</th><th></th></tr></thead>
              <tbody>
                {subscriptions.map(sub => (
                  <tr key={sub._id}>
                    <td>{sub.token}</td>
                    <td>{sub.chatId || '—'}</td>
                    <td>{sub.isActive ? '✅ Да' : '❌ Нет'}</td>
                    <td>{new Date(sub.subscribedAt).toLocaleDateString()}</td>
                    <td><button onClick={() => handleDeleteSubscription(sub._id)}>Удалить</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="telegram-info">
            <h3>Инструкция для пользователей</h3>
            <p>1. Пользователь получает токен объекта в настройках.<br/>
            2. Добавляет токен боту командой <code>/add &lt;токен&gt;</code>.<br/>
            3. Бот будет присылать уведомления о выходе параметров за границы и их нормализации (не чаще 1 раза в 5 минут).<br/>
            4. Для отписки: <code>/erase &lt;токен&gt;</code>.<br/>
            5. Команда <code>/stat</code> показывает текущие диапазоны для всех подключённых объектов.<br/>
            6. Команда <code>/info</code> выводит Chat ID для идентификации.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;